import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MapPin,
  Phone,
  UserMinus,
  UserX,
} from 'lucide-react';
import { fetchJourJ, pointerPresence, type JourJPoste } from '../services/api';

const jourLong = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** Les trois réponses possibles, et leur allure une fois posées. */
const REPONSES = [
  { code: 'arrive', label: 'Arrivé', icone: CheckCircle2, actif: 'bg-green-600 text-white border-green-600' },
  { code: 'absent', label: 'Absent', icone: UserX, actif: 'bg-red-600 text-white border-red-600' },
  { code: 'remplace', label: 'Remplacé', icone: UserMinus, actif: 'bg-amber-600 text-white border-amber-600' },
] as const;

/**
 * Le jour J : qui est là, qui manque, comment le joindre.
 *
 * Le planning se prépare des semaines à l'avance, sur un écran large, avec une
 * grille. Le soir de la fête, rien de tout cela ne sert : on est debout
 * derrière un bar, on a un téléphone dans une main, et la seule question qui
 * se pose est « il est où, celui du snack ? ».
 *
 * Cet écran ne montre donc qu'une journée, poste par poste. Un nom, un numéro
 * qu'on appelle d'un doigt, trois boutons larges. Rien à remplir, rien à
 * faire défiler latéralement, rien qui demande de réfléchir à ce qu'on
 * cherche.
 *
 * Ce qui n'est pas pointé reste « pas encore », jamais « absent » : une équipe
 * dont personne n'a eu le temps de cocher les cases n'est pas une équipe
 * défaillante, et l'écran ne doit pas la faire passer pour telle.
 */
export default function JourJPage() {
  const { id } = useParams<{ id: string }>();
  const evenementId = Number(id);
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);

  const jour = params.get('jour') ?? undefined;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['jour-j', evenementId, jour],
    queryFn: () => fetchJourJ(evenementId, jour),
    enabled: Number.isFinite(evenementId),
    retry: false,
    // La feuille se regarde à plusieurs, chacun sur son téléphone : ce que
    // pointe le responsable du bar doit paraître chez le président.
    refetchInterval: 60 * 1000,
  });

  const pointer = useMutation({
    mutationFn: ({ creneauId, presence }: { creneauId: number; presence: string }) =>
      pointerPresence(evenementId, creneauId, presence),
    onSuccess: () => {
      setErreur(null);
      queryClient.invalidateQueries({ queryKey: ['jour-j', evenementId] });
    },
    onError: (e: Error) => setErreur(e.message),
  });

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 space-y-4">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">{(error as Error).message}</p>
        </div>
        <Link to="/bureau/evenements" className="btn-outline inline-flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour aux événements
        </Link>
      </div>
    );
  }

  const { evenement, postes, jours, chiffres } = data;
  const jourAffiche = data.jour;

  return (
    <div className="p-4 lg:p-0 space-y-4 pb-24 lg:pb-0">
      <div>
        <Link
          to={`/bureau/evenements/${evenementId}?onglet=planning`}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-700 dark:text-gray-400"
        >
          <ArrowLeft className="w-4 h-4" /> Planning
        </Link>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
          Jour J — {jourLong.format(new Date(`${jourAffiche}T12:00:00`))}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-x-2">
          {evenement.nom}
          {evenement.lieu && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {evenement.lieu}
            </span>
          )}
        </p>
      </div>

      {/* La journée se change ici : le montage de la veille et le rangement du
          lendemain se pointent comme le reste. */}
      {jours.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {jours.map((j) => {
            const actif = j === jourAffiche;
            return (
              <button
                key={j}
                type="button"
                onClick={() => setParams(j === jours[0] ? {} : { jour: j })}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border whitespace-nowrap ${
                  actif
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric' }).format(
                  new Date(`${j}T12:00:00`)
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Le compte, en une ligne : ce qu'on regarde toutes les demi-heures. */}
      <div className="grid grid-cols-3 gap-2">
        <Chiffre libelle="Postes tenus" valeur={`${chiffres.pointes}/${chiffres.total}`} />
        <Chiffre libelle="Absents" valeur={String(chiffres.absents)} alerte={chiffres.absents > 0} />
        <Chiffre libelle="À pointer" valeur={String(chiffres.total - chiffres.pointes)} />
      </div>

      {erreur && (
        <div className="card bg-red-50 border-red-200 text-red-800 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p>{erreur}</p>
        </div>
      )}

      {postes.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun créneau ce jour-là. Les postes se posent depuis le planning de l'événement.
          </p>
        </div>
      ) : (
        postes.map((poste) => (
          <Poste
            key={poste.code}
            poste={poste}
            enCours={pointer.isPending}
            onPointer={(creneauId, presence) => pointer.mutate({ creneauId, presence })}
          />
        ))
      )}
    </div>
  );
}

function Chiffre({
  libelle,
  valeur,
  alerte = false,
}: {
  libelle: string;
  valeur: string;
  alerte?: boolean;
}) {
  return (
    <div className={`card py-3 text-center ${alerte ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : ''}`}>
      <p className="text-xs text-gray-500 dark:text-gray-400">{libelle}</p>
      <p
        className={`text-xl font-bold tabular-nums ${
          alerte ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {valeur}
      </p>
    </div>
  );
}

/**
 * Un poste et son équipe.
 *
 * Les boutons sont larges parce qu'on les touche avec un pouce, debout, dans
 * le noir. Le numéro est un lien parce qu'à ce moment-là on n'a pas le temps
 * de le recopier.
 */
function Poste({
  poste,
  enCours,
  onPointer,
}: {
  poste: JourJPoste;
  enCours: boolean;
  onPointer: (creneauId: number, presence: string) => void;
}) {
  const manquants = poste.lignes.filter((l) => l.presence === 'absent').length;

  return (
    <section className="card space-y-3">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        {poste.libelle}
        <span className="text-sm font-normal text-gray-500">{poste.lignes.length}</span>
        {manquants > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
            {manquants} absent{manquants > 1 ? 's' : ''}
          </span>
        )}
      </h2>

      <ul className="space-y-3">
        {poste.lignes.map((ligne) => (
          <li key={ligne.id} className="space-y-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-medium text-gray-900 dark:text-gray-100">{ligne.nom}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                {ligne.debut} – {ligne.fin}
              </span>
              {ligne.statutConfirmation === 'propose' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  n'a jamais confirmé
                </span>
              )}
              {ligne.telephone && (
                <a
                  href={`tel:${ligne.telephone.replace(/\s/g, '')}`}
                  className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary-300"
                >
                  <Phone className="w-4 h-4" /> {ligne.telephone}
                </a>
              )}
            </div>

            {ligne.commentaire && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{ligne.commentaire}</p>
            )}

            {/* Retoucher le même bouton efface le pointage : un doigt dérape,
                et revenir en arrière ne doit rien demander de plus. */}
            <div className="grid grid-cols-3 gap-2">
              {REPONSES.map(({ code, label, icone: Icone, actif }) => {
                const choisi = ligne.presence === code;
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={enCours}
                    onClick={() => onPointer(ligne.id, code)}
                    aria-pressed={choisi}
                    className={`inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
                      choisi
                        ? actif
                        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <Icone className="w-4 h-4 shrink-0" />
                    {label}
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
